import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";

const CONTACT_EMAIL = "admin@whatsyourbalance.com";
const EFFECTIVE_DATE = "February 27, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen" data-testid="marketing-privacy">
      <MarketingHeader />
      <section className="max-w-3xl mx-auto px-6 md:px-8 py-20">
        <div className="mb-10">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Legal</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#2C302E]">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#8A918D] mt-2">Effective {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-neutral max-w-none space-y-8 text-[#2C302E] leading-relaxed">
          <div className="rounded-2xl border border-[#D8CFB9] bg-[#FBF7F0] p-4 text-sm text-[#59605C]">
            What&rsquo;s Your Balance? is currently in a private beta. This policy explains what we collect, how we use it,
            and how you can control it. It will be updated before a public launch.
          </div>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">1. What we collect</h2>
            <p className="mb-2">To run the service for you, we store the following in our database:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Account information: your email, a hashed password, and (optionally) your name and Google profile picture if you sign in with Google.</li>
              <li>Cash-flow data you enter: funding-account labels and balances, bills, income sources, pending charges, debts, and any notes you add.</li>
              <li>App preferences: onboarding status, cushion thresholds, monthly-summary opt-in, and role (user or admin).</li>
              <li>Subscription state: trial status, subscription status, Stripe customer/session identifiers. Payment card details are stored by Stripe, not by us.</li>
              <li>Feedback you submit through the in-app widget: message text, category, the page you were on, and your email address so we can follow up.</li>
              <li>Basic operational logs (request timestamps, error traces) needed to keep the service running.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">2. What we do NOT collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your bank credentials. The service does not connect to your bank; you enter balances manually.</li>
              <li>Full credit card numbers. Payments run through Stripe, which handles card data under its own compliance.</li>
              <li>Your Social Security number, tax records, or government IDs.</li>
              <li>Location data, contacts, or files from your device.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">3. How we use your data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Run the app: show your dashboard, pay-period forecasts, bills, and monthly outlook.</li>
              <li>Authenticate you and protect your account.</li>
              <li>Bill you through Stripe if you subscribe.</li>
              <li>Respond to feedback or support requests you send us.</li>
              <li>Fix bugs and improve the product based on aggregated, non-personal patterns.</li>
            </ul>
            <p className="mt-2">We do not sell your data. We do not run ads. We do not share your cash-flow entries with third parties.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">4. Who processes your data</h2>
            <p>These are the third parties involved in running the service:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Cloud hosting</strong> — runs the app servers and database on our behalf.</li>
              <li><strong>MongoDB</strong> — stores your data at rest.</li>
              <li><strong>Stripe</strong> — processes subscription payments. We only receive non-sensitive identifiers (customer id, session id, subscription status).</li>
              <li><strong>Google</strong> — if you choose Google sign-in, Google authenticates you and shares your email, name, and profile picture with us.</li>
            </ul>
            <p className="mt-2">Each of these providers has its own privacy policy governing what they do with the data they handle.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">5. Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Passwords are stored as bcrypt hashes — the plain password is never saved.</li>
              <li>Sessions use httpOnly cookies over HTTPS, so JavaScript running in the browser cannot read the token.</li>
              <li>All traffic between your browser and our servers is encrypted with TLS.</li>
              <li>Data is stored per-user; user records are scoped and filtered by user id on every query.</li>
            </ul>
            <p className="mt-2">
              No system is 100% secure. If we ever become aware of a breach affecting your account we will notify you promptly.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">6. Cookies</h2>
            <p>We use only the cookies needed to keep you signed in: an access token cookie and a refresh token cookie (both httpOnly). We do not use tracking or advertising cookies.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">7. Your choices &amp; rights</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Access &amp; export</strong> — you can view all of your data inside the app. Request an export at any time by emailing us.</li>
              <li><strong>Correct or update</strong> — edit any bill, income, funding source, or account setting directly in the app.</li>
              <li><strong>Delete</strong> — email us to close your account and remove your data. We will confirm and then delete within 30 days, except records we must retain for tax or fraud reasons (payment history, minimal audit logs).</li>
              <li><strong>Opt out of the monthly summary email</strong> — via the toggle in Settings.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">8. Retention</h2>
            <p>
              We keep your data as long as your account is active. If you close your account, we remove your app data
              within 30 days. Payment records that we&rsquo;re legally required to keep may be retained longer.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">9. Children</h2>
            <p>The service is intended for adults managing their own money. It is not directed to children under 13, and we do not knowingly collect data from them.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">10. Changes to this policy</h2>
            <p>
              We may update this policy as the product evolves — for example when a new integration is added. Material
              changes will be notified inside the app or by email before they take effect.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">11. Contact</h2>
            <p>
              Privacy questions, data-export requests, or account-deletion requests? Email
              {" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#4A6148] underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
